<template>
  <div class="content-grid-list w-full rounded-2 flex bg-base p-4">
    <div class="flex flex-col w-full">
      <ContentGridListItem
        v-for="(item, index) in items"
        :key="getItemKey(item, index)"
        :is-last="index === items.length - 1"
        :item="item"
        :used-in-section-id="usedInSectionId"
        @click="(e: MouseEvent) => onItemClick(item, e)"
        @deleted-file-clicked="emit('deletedFileClicked')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { omit } from 'lodash-es'

import ContentGridListItem from './ContentGridListItem.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { isModifierClick } from '@lib/util/navigation.util'
import type { ContentGridItemFileProps, ContentGridItemSlideProps, FileOpenParams } from '@canvas-builder/types/canvas'
import type { CanvasRetrieve } from '@openapi'

const props = defineProps<{
  items: (ContentGridItemSlideProps | ContentGridItemFileProps)[]
  usedInSectionId?: CanvasRetrieve['id']
}>()

const emit = defineEmits<{
  (e: 'deletedFileClicked'): void
}>()

const { onFileOpen } = useCanvas()

// Get unique key for grid items
function getItemKey(item: ContentGridItemSlideProps | ContentGridItemFileProps, index: number): string {
  if (item.type === 'slide') {
    const fileId = item.file?.id || `unknown-file-${index}`
    const slideIndex = item.slide?.index ?? index
    return `${fileId}-page-${slideIndex}`
  } else {
    return item.file?.id || `file-${index}`
  }
}

function onItemClick(item: ContentGridItemSlideProps | ContentGridItemFileProps, e?: MouseEvent) {
  if (!item?.file || item.file?.is_deleted) return

  const payload = {
    newTab: e ? isModifierClick(e) : false,
    source_file_id: item.file.id,
    page_index: item.type === 'slide' ? item.slide.index : undefined,
    ...omit(item.file, 'id'),
  } as FileOpenParams

  if (props.usedInSectionId) {
    payload.used_in_section_id = props.usedInSectionId
  }

  // always add file_id as a fallback
  payload.file_id = item.file.snapshot_id ?? item.file.id

  onFileOpen.value?.(payload)
}
</script>

<style scoped>
.content-grid-list {
  max-height: 600px;
  overflow-y: auto;
}
</style>
